import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { API_URL } from '../../services/api';
import { DataState } from '../../context/DataProvider';

const TeamsWebhooksManager = () => {
  const { userGoogleId } = DataState();
  const [loading, setLoading] = useState(true);
  const [toggleLoading, setToggleLoading] = useState(null);

  const [teams, setTeams] = useState([]);
  const [channels, setChannels] = useState([]);
  const [chats, setChats] = useState([]);

  useEffect(() => {
    async function fetchStatus() {
      try {
        const res = await fetch(
          `${API_URL}/microsoft_teams/teams-status`,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${userGoogleId}`,
            },
            credentials: 'include',
          }
        );
        const json = await res.json();
        if (!json.success) {
          throw new Error(json.message || 'Failed to load Teams data');
        }
        setTeams(json.data.teams);
        setChannels(json.data.channels);
        setChats(json.data.chats);
      } catch (err) {
        toast.error(`Error: ${err.message}`);
      } finally {
        setLoading(false);
      }
    }
    fetchStatus();
  }, [userGoogleId]);

  const handleToggle = async (type, identifiers) => {
    // type: 'team' | 'channel' | 'chat'
    // identifiers: { team_id } | { team_id, channel_id } | { chat_id }
    const key =
      type === 'chat'
        ? identifiers.chat_id
        : type === 'channel'
        ? identifiers.channel_id
        : identifiers.team_id;

    setToggleLoading(key);
    try {
      const res = await fetch(
        `${API_URL}/microsoft_teams/webhook/toggle`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${userGoogleId}`,
          },
          credentials: 'include',
          body: JSON.stringify(identifiers),
        }
      );
      const json = await res.json();
      if (!json.success) {
        throw new Error(json.message || 'Toggle failed');
      }

      // Update the right list
      const updater = (prev) =>
        prev.map((item) => {
          const match =
            (type === 'chat' && item.chat_id === identifiers.chat_id) ||
            (type === 'team' && item.team_id === identifiers.team_id && !identifiers.channel_id) ||
            (type === 'channel' &&
              item.team_id === identifiers.team_id &&
              item.channel_id === identifiers.channel_id);
          if (!match) return item;
          return {
            ...item,
            webhook_exists: json.webhook_exists,
            subscription_ids: json.subscription_ids || item.subscription_ids,
          };
        });

      if (type === 'team') setTeams(updater);
      else if (type === 'channel') setChannels(updater);
      else setChats(updater);

      toast.success(
        `Webhooks ${json.webhook_exists ? 'enabled' : 'disabled'} for this ${type}!`
      );
    } catch (err) {
      toast.error(`Error: ${err.message}`);
    } finally {
      setToggleLoading(null);
    }
  };

  // Loader rows
  const loaderRows = () =>
    [...Array(3)].map((_, i) => (
      <tr key={i} className="animate-pulse">
        <td className="px-4 py-3">
          <div className="h-4 bg-zinc-700 rounded w-3/4" />
        </td>
        <td className="px-4 py-3">
          <div className="h-4 bg-zinc-700 rounded w-2/3" />
        </td>
        <td className="px-4 py-3 text-center">
          <div className="h-8 bg-zinc-700 rounded w-24 mx-auto" />
        </td>
      </tr>
    ));

  const renderTable = (items, type) => (
    <div className="mb-8">
      <h2 className="text-lg font-semibold mb-2">
        {type === 'team'
          ? 'Teams'
          : type === 'channel'
          ? 'Channels'
          : 'Chats'}
      </h2>
      <div className="overflow-x-auto">
        <table className="w-full bg-zinc-800 rounded-lg">
          <thead className="bg-zinc-800">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase text-zinc-400">
                {type === 'chat' ? 'Topic / Chat ID' : 'Name / ID'}
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase text-zinc-400">
                Status
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium uppercase text-zinc-400">
                Action
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-700">
            {loading
              ? loaderRows()
              : items.map((it) => {
                  const id =
                    type === 'chat'
                      ? it.chat_id
                      : type === 'channel'
                      ? it.channel_id
                      : it.team_id;
                  const label =
                    type === 'chat'
                      ? it.topic || it.chat_id
                      : it.displayName || id;
                  return (
                    <tr
                      key={id}
                      className="hover:bg-zinc-700 transition duration-150"
                    >
                      {/* <td className="px-4 py-3 text-sm">{label}</td> */}
                       <td className="px-4 py-3 text-xs">
                        <div className="font-medium">{label}</div>
                        {it.members && (
                          <div className="text-xs text-zinc-400 mt-1">
                            {it.members.map(p => p.name || p.id).join(', ')}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs">
                        {it.webhook_exists ? 'Enabled' : 'Disabled'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() =>
                            handleToggle(type, {
                              ...(type === 'chat'
                                ? { chat_id: id }
                                : { team_id: it.team_id, ...(type === 'channel' && { channel_id: id }) }),
                            })
                          }
                          disabled={toggleLoading !== null}
                          className={`px-4 py-2 rounded-md shadow text-xs transform transition duration-200 focus:outline-none focus:ring ${
                            it.webhook_exists
                              ? 'bg-red-600 hover:bg-red-700'
                              : 'bg-green-600 hover:bg-green-700'
                          } text-white disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                          {toggleLoading === id ? (
                            <svg
                              className="animate-spin h-5 w-5 inline-block"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              />
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8v8H4z"
                              />
                            </svg>
                          ) : it.webhook_exists ? (
                            'Delete'
                          ) : (
                            'Create'
                          )}
                        </button>
                      </td>
                    </tr>
                  );
                })}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="py-8 px-4 bg-zinc-900 text-zinc-300 flex justify-center">
      {/* Added max height and scroll */}
      <div className="w-full max-w-4xl max-h-[70vh] overflow-y-auto space-y-8">
        <header className="mb-6">
          <h1 className="text-lg font-bold">Microsoft Teams Webhooks</h1>
          <p className="mt-1 text-zinc-400">
            Toggle subscriptions for Teams, Channels, and Chats.
          </p>
        </header>
        {renderTable(teams, 'team')}
        {renderTable(channels, 'channel')}
        {renderTable(chats, 'chat')}
      </div>
    </div>
  );
};

export default TeamsWebhooksManager;
